import { HTMLAttributes, useCallback, useEffect, useRef } from 'react'
import SplitText from '@activetheory/split-text'
import gsap from 'gsap'

import { cn } from '@/lib/utils'

export type TextEffectProps = HTMLAttributes<HTMLButtonElement> &
  Partial<{
    className?: string
  }>

const TextEffect = ({ className, children, ...props }: TextEffectProps) => {
  const textWrapRef = useRef<HTMLSpanElement>(null)

  const handleMouseLeave = useCallback(() => {
    const chars = textWrapRef.current?.querySelectorAll('.char')
    if (!chars) return

    const charCount = chars.length
    const baseDuration = 0.3
    const maxDuration = 0.7
    const duration = Math.min(baseDuration + charCount * 0.01, maxDuration)

    gsap.to(chars, {
      y: '0%',
      duration: duration,
      stagger: 0.015,
      ease: 'power3.out'
    })
  }, [])

  const handleMouseEnter = useCallback(() => {
    const chars = textWrapRef.current?.querySelectorAll('.char')
    if (!chars) return

    const charCount = chars.length
    const baseDuration = 0.3
    const maxDuration = 0.7
    const duration = Math.min(baseDuration + charCount * 0.01, maxDuration)

    gsap.to(chars, {
      y: '-100%',
      duration: duration,
      stagger: 0.015,
      ease: 'power3.out'
    })
  }, [])

  useEffect(() => {
    if (!textWrapRef.current) return
    const btnRoot =
      textWrapRef.current?.closest('.group') || textWrapRef.current
    btnRoot?.addEventListener('mouseenter', handleMouseEnter)
    btnRoot?.addEventListener('mouseleave', handleMouseLeave)
    const splitText = new SplitText(textWrapRef.current, {
      type: 'chars',
      noBalance: true
    })

    return () => {
      splitText.revert()
      btnRoot?.removeEventListener('mouseenter', handleMouseEnter)
      btnRoot?.removeEventListener('mouseleave', handleMouseLeave)
    }
  }, [handleMouseEnter, handleMouseLeave])

  return (
    <span
      className={cn(
        'overflow-hidden [&_span]:text-current',
        '[text-shadow:0px_2.25rem_currentColor]',
        className
      )}
      ref={textWrapRef}
      {...props}
    >
      {children}
    </span>
  )
}

export default TextEffect
